<script setup lang="ts">
	import { useIntersectionObserver } from '@vueuse/core'
	import StringMethods from '~/utils/character'
	import type { StatsSchema } from '@/types/Activities'
	import type { ClassContext } from '@/types/Config'

	withDefaults(
		defineProps<{
			stats: Array<StatsSchema>
			totalClass?: string
			cardClass?: ClassContext
			disableOverflow?: boolean
			noIndicator?: boolean
		}>(),
		{
			noIndicator: false
		}
	)
	const emit = defineEmits(['key'])
	const slots = useSlots()
	const { isMobile } = useMobile()
	const replace = StringMethods()
	const scrollEvent = ref()
	const isBoxVisible = ref(false)
	const last_box = ref<HTMLDivElement | null>(null)

	useIntersectionObserver(last_box, ([{ isIntersecting }]) => {
		isBoxVisible.value = isIntersecting
	})
	onMount(() => {
		if (last_box.value) {
			const el = last_box.value.querySelector("[data-id='stat-overflow']") as HTMLDivElement
			if (el) {
				scrollEvent.value = el.addEventListener('scrollend', () => {
					isBoxVisible.value = Number(el?.offsetWidth) + Number(el?.scrollLeft) !== Number(el?.scrollWidth)
				})
			}
		}
	})
	onUnmounted(() => {
		removeEventListener('scrollend', scrollEvent.value)
		isBoxVisible.value = isMobile.value
	})
</script>

<template>
	<header
		ref="last_box"
		:class="{
			'flex items-center gap-x-5 overflow-x-auto lg:flex-nowrap': slots.prepend || slots.append
		}"
		class="relative"
	>
		<div
			v-if="isBoxVisible && !noIndicator"
			class="animate-x w-50 absolute right-0 top-14 z-50 h-fit w-fit sm:hidden"
		>
			<lazy-tools-icon dir="arrows" icon="double-greater-than" :height="40" :width="40" />
		</div>
		<slot name="prepend" />
		<shared-card
			default-class="min-h-[174px] w-full lg:flex hidden px-5 relative items-center justify-around pt-2 rounded-xl border border-gray-200"
			:class="cardClass"
			:ui="{ container: 'shadow-none', wrapper: 'max-w-full' }"
		>
			<div class="overflow-x- relative flex w-full flex-none justify-center sm:justify-between">
				<slot name="prepend-card" />
				<div
					v-for="(item, index) in stats"
					:key="index"
					class="group relative w-full border-r px-4 transition ease-in-out first:px-0 last:border-r-0 md:flex-1"
					@click="emit('key', item.key)"
				>
					<a-tooltip color="white" :arrow-point-at-center="true">
						<slot
							:name="item.key"
							:data-items="item"
							:item-class="{
								total: `text-[1em] lg:text-[32px] xl:text-[40px] font-normal tracking-wide text-blue-950 transition-all ease-in-out group-hover:text-primary-2  ${item?.totalClass}`,
								title: `text-sm text-gray-500 font-medium whitespace-pre mb-4 tracking-wide ${item?.titleClass}`
							}"
						>
							<div class="space-y-4" :class="item?.containerClass">
								<h4
									class="whitespace-pre text-sm font-medium tracking-wide text-gray-500"
									:class="item?.titleClass"
								>
									{{ item.title }}
								</h4>

								<div
									class="mb-4 text-[1em] font-medium text-blue-950 transition-all ease-in-out group-hover:text-primary-2 lg:text-[32px] xl:text-[40px]"
									:class="[totalClass, item?.totalClass]"
								>
									{{ item.total }}
								</div>
								<slot :name="`${item.key}-p`" />
							</div>
						</slot>

						<template v-if="item.toolTipMessage" #title>
							<span
								class="font-medium text-gray-600"
								v-html="replace.replacePlaceholder(item.toolTipMessage, item.title)"
							/>
						</template>
					</a-tooltip>
				</div>
			</div>
		</shared-card>

		<div
			class="relative flex w-full flex-col justify-center rounded-lg bg-transparent lg:hidden"
			:class="cardClass"
		>
			<div
				class="outers relative z-0 mx-auto flex w-full flex-1 snap-x snap-mandatory flex-nowrap gap-5"
				:class="{ 'overflow-x-auto': !disableOverflow }"
				data-id="stat-overflow"
			>
				<slot name="prepend-card" />
				<div
					v-for="(item, index) in stats"
					:key="index"
					class="inner group relative min-w-[200px] flex-shrink-0 snap-center rounded-lg py-6 pl-3 transition ease-in-out md:flex-1"
					:class="[item?.containerClass || 'border']"
					@click="emit('key', item.key)"
				>
					<a-tooltip color="white" :arrow-point-at-center="true">
						<slot
							:name="item.key"
							:data-items="item"
							:item-class="{
								total: 'text-[2em] lg:text-[32px] xl:text-[32px] font-medium text-blue-950 transition-all ease-in-out group-hover:text-primary-2 ',
								title: 'mb-4 text-sm text-gray-500 font-medium lg:mb-8'
							}"
						>
							<div class="space-y-3">
								<h4 class="mb-4 whitespace-pre font-medium text-gray-500" :class="item?.titleClass">
									{{ item.title }}
								</h4>

								<div
									class="text-[2em] font-medium text-gray-600 transition-all ease-in-out group-hover:text-primary-2 md:text-4xl lg:text-[32px] xl:text-[32px]"
									:class="[totalClass, item.totalClass]"
								>
									{{ item.total }}
								</div>
							</div>
						</slot>

						<template v-if="item.toolTipMessage" #title>
							<span
								class="font-medium text-gray-600"
								v-html="replace.replacePlaceholder(item.toolTipMessage, item.title)"
							/>
						</template>
					</a-tooltip>
				</div>
			</div>
		</div>
		<slot name="append" />
	</header>
</template>

<style scoped>
	.animate-x {
		animation: moveX 2s linear infinite;
		transition: 1s linear;
		transform-origin: center;
		transform-style: preserve-3d;
	}
</style>
